// @ts-nocheck
import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';

export interface FraudRule {
  id: string;
  rule_name: string;
  rule_type: 'self_purchase' | 'velocity' | 'ip_match' | 'disposable_email' | 'chargeback_rate';
  threshold_value: number;
  action: 'flag' | 'block' | 'manual_review';
  is_active: boolean;
  created_at: string;
}

export interface FraudFlag {
  id: string;
  affiliate_id: string;
  conversion_id: string | null;
  rule_id: string;
  flag_reason: string;
  severity: 'low' | 'medium' | 'high';
  status: 'pending' | 'cleared' | 'confirmed';
  created_at: string;
  rule?: FraudRule;
}

export interface FraudScore {
  affiliate_id: string;
  score: number;
  last_updated: string;
}

export const useFraudManagement = () => {
  const { profile } = useAuth();
  const [rules, setRules] = useState<FraudRule[]>([]);
  const [flags, setFlags] = useState<FraudFlag[]>([]);
  const [fraudScore, setFraudScore] = useState<FraudScore[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch fraud rules
      const { data: rulesData, error: rulesError } = await supabase
        .from('affiliate_fraud_rules')
        .select('*')
        .order('created_at', { ascending: false });

      if (rulesError) throw rulesError;

      // Fetch fraud flags
      const { data: flagsData, error: flagsError } = await supabase
        .from('affiliate_fraud_flags')
        .select(`
          *,
          rule:affiliate_fraud_rules(*)
        `)
        .order('created_at', { ascending: false });

      if (flagsError) throw flagsError;

      // Fetch fraud scores
      const { data: scoresData, error: scoresError } = await supabase
        .from('affiliate_fraud_scores')
        .select('*');

      if (scoresError) throw scoresError;

      setRules(rulesData || []);
      setFlags(flagsData || []);
      setFraudScore(scoresData || []);

    } catch (error) {
      console.error('Error fetching fraud management data:', error);
      setError(error instanceof Error ? error.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const createRule = async (ruleData: Omit<FraudRule, 'id' | 'created_at'>) => {
    try {
      const { data, error } = await supabase
        .from('affiliate_fraud_rules')
        .insert([ruleData])
        .select()
        .single();

      if (error) throw error;

      await fetchData();
      return data;
    } catch (error) {
      console.error('Error creating fraud rule:', error);
      throw error;
    }
  };

  const updateRule = async (ruleId: string, updates: Partial<FraudRule>) => {
    try {
      const { data, error } = await supabase
        .from('affiliate_fraud_rules')
        .update(updates)
        .eq('id', ruleId)
        .select()
        .single();

      if (error) throw error;

      await fetchData();
      return data;
    } catch (error) {
      console.error('Error updating fraud rule:', error);
      throw error;
    }
  };

  const deleteRule = async (ruleId: string) => {
    try {
      const { error } = await supabase
        .from('affiliate_fraud_rules')
        .delete()
        .eq('id', ruleId);

      if (error) throw error;

      await fetchData();
    } catch (error) {
      console.error('Error deleting fraud rule:', error);
      throw error;
    }
  };

  const updateFlagStatus = async (flagId: string, status: FraudFlag['status']) => {
    try {
      const { data, error } = await supabase
        .from('affiliate_fraud_flags')
        .update({ status })
        .eq('id', flagId)
        .select()
        .single();

      if (error) throw error;

      await fetchData();
      return data;
    } catch (error) {
      console.error('Error updating fraud flag status:', error);
      throw error;
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  return {
    rules,
    flags,
    fraudScore,
    loading,
    error,
    createRule,
    updateRule,
    deleteRule,
    updateFlagStatus,
    refetch: fetchData
  };
};